import { Brain } from "lucide-react";
import { REASONING_EFFORT_LABELS, REASONING_EFFORTS } from "@/constants/reasoningEffort";
import type { ReasoningConfig, ReasoningEffort } from "@/types";

export interface ReasoningEffortPickerProps {
  /** The active model's curated allowlist; null/empty hides the picker. */
  config: ReasoningConfig | null | undefined;
  /** Stored effort; null = provider default. */
  value: string | null;
  onChange: (effort: string | null) => void;
  disabled?: boolean;
}

/**
 * Reasoning effort for the current chat (MHS-669). Lists only what the model
 * accepts, in the canonical order; "Default" sends no parameter at all.
 */
export function ReasoningEffortPicker({
  config,
  value,
  onChange,
  disabled = false,
}: Readonly<ReasoningEffortPickerProps>) {
  const supported = REASONING_EFFORTS.filter((e) => config?.supportedEfforts.includes(e));
  if (supported.length === 0) return null;

  const defaultLabel = config?.defaultEffort ? `Default (${REASONING_EFFORT_LABELS[config.defaultEffort]})` : "Default";
  const current = value && supported.includes(value as ReasoningEffort) ? value : "";

  return (
    <div className="flex items-center gap-1.5 pl-3 pr-2 py-2 rounded-lg border bg-surface-secondary border-border-secondary text-content-primary">
      <Brain className="w-4 h-4 text-content-tertiary shrink-0" aria-hidden="true" />
      <select
        aria-label="Reasoning effort"
        title="Reasoning effort"
        value={current}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value || null)}
        className="bg-transparent text-sm font-medium focus:outline-hidden disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
      >
        <option value="">{defaultLabel}</option>
        {supported.map((effort) => (
          <option key={effort} value={effort}>
            {REASONING_EFFORT_LABELS[effort]}
          </option>
        ))}
      </select>
    </div>
  );
}
